import apiClient from './api.service';

/**
 * After password/OTP login, `/auth/me` returns the full user (e.g. `authorityLevel`) for staff apps.
 * @param {string|null|undefined} schoolIdForMeQuery — Parent multi-school: pass selected school so `schoolAccess` matches context.
 */
async function hydrateStoredUserFromMe(schoolIdForMeQuery = undefined) {
  try {
    const me = await apiClient.get(
      '/auth/me',
      schoolIdForMeQuery ? { params: { schoolId: schoolIdForMeQuery } } : undefined
    );
    if (!me.data?.success || !me.data?.user) return;
    const u = me.data.user;
    const prevStr = localStorage.getItem('user');
    const prev = prevStr ? JSON.parse(prevStr) : {};
    const id = u._id != null ? String(u._id) : (u.id != null ? String(u.id) : prev.id);
    let schoolId = prev.schoolId;
    if (u.schoolId != null) {
      if (typeof u.schoolId === 'object' && u.schoolId._id != null) {
        schoolId = String(u.schoolId._id);
      } else {
        schoolId = String(u.schoolId);
      }
    }
    const merged = {
      ...prev,
      id,
      name: u.name,
      email: u.email,
      mobile: u.mobile,
      role: u.role,
      schoolId,
      photo: u.photo,
      authorityLevel: u.authorityLevel,
    };
    if (me.data.schoolAccess) {
      merged.schoolReadOnly = !!me.data.schoolAccess.schoolReadOnly;
    } else {
      delete merged.schoolReadOnly;
    }
    localStorage.setItem('user', JSON.stringify(merged));
  } catch (_) {
    /* keep login payload if /me fails */
  }
}

export const authService = {
  // Email login (Super Admin)
  loginWithEmail: async (email, password) => {
    const response = await apiClient.post('/auth/login/email', { email, password });
    if (response.data.success) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // School staff login (school code + email + password)
  loginWithSchool: async (schoolCode, email, password) => {
    const response = await apiClient.post('/auth/login', { schoolCode, email, password });
    if (response.data.success) {
      localStorage.setItem('token', response.data.token);
      const u = { ...response.data.user, schoolReadOnly: !!response.data.schoolReadOnly };
      localStorage.setItem('user', JSON.stringify(u));
      await hydrateStoredUserFromMe();
    }
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.put('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },

  forgotPassword: async (email, schoolCode) => {
    const response = await apiClient.post('/auth/forgot-password', { email, schoolCode });
    return response.data;
  },

  resetPassword: async (email, schoolCode, token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', { email, schoolCode, token, newPassword });
    return response.data;
  },

  // Send OTP (schoolCode optional - for staff login; role optional - e.g. 'PARENT')
  sendOTP: async (mobile, email = null, schoolCode = null, role = null) => {
    const response = await apiClient.post('/auth/otp/send', { mobile, email, schoolCode, role });
    return response.data;
  },

  // Verify OTP (schoolCode optional - for staff login; role optional - e.g. 'PARENT')
  verifyOTP: async (mobile, otp, schoolCode = null, role = null) => {
    const response = await apiClient.post('/auth/otp/verify', { mobile, otp, schoolCode, role });
    if (response.data.success) {
      localStorage.setItem('token', response.data.token);
      const u = { ...response.data.user, schoolReadOnly: !!response.data.schoolReadOnly };
      localStorage.setItem('user', JSON.stringify(u));
      await hydrateStoredUserFromMe();
    }
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Re-fetch `/auth/me` and merge into `localStorage` user (e.g. after parent switches school).
   * @param {{ schoolId?: string|null }} [options]
   */
  refreshSessionUserProfile: async (options = {}) => {
    await hydrateStoredUserFromMe(options.schoolId || undefined);
  },

  // Logout
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Get stored user
  getStoredUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
};
